// BackendPageForSA.js — the "Small Automations" page.
//
// Reads its data from <ConfigContext> (App.js fetches the WHOLE website dict
// once at first load — navigating here makes no backend call). Content lives in
// website_items_dev/3_small_automations/small_automations.yaml — edit + refresh,
// no rebuild. Items appear when a yaml key matches an icon in graphic_items/.
//
// Layout per item (from website_design/ideas_for_other_pages/small_automations_v1.png):
//   [square icon] | title
//                 | header (italic via the _..._ markup in the yaml)
//                 | FOR EXAMPLE label + example pills (list, in yaml order; max 2 per row)
//   body — full card width, under everything (NOT indented to the icon column)
//   On mobile the examples block moves OUT of the icon column: centered under the
//   icon/title/header group, pills stacked vertically (the block is rendered twice,
//   one variant per breakpoint; CSS shows the right one).
//
// Icon size: square default from yaml format.images (per breakpoint), tweakable
// per item via image.format_override (width/height) → arrives as item.image_size.
//
// Each card carries id=<yaml tag> so the homepage boxes can deep-link to
// /small-automations#<tag> (scroll handled by the hash effect below).

import React, { useContext, useEffect } from 'react';
import { ConfigContext } from '../../App';
import { OrangeButton } from '../../myReactLib/BasicItems';
import {
  Navigation,
  Footer,
  getText,
  getStyle,
  getBottomMargin,
  RT,
} from '../../myReactLib/MainBlocks';

function BackendPageForSA() {
  const { config, homepageData, smallAutomationsData, contactData } = useContext(ConfigContext);

  // deep-link support: /small-automations#<tag> scrolls to that card
  useEffect(() => {
    const hash = window.location.hash.replace('#', '');
    if (!hash) return;
    const el = document.getElementById(hash);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  if (!homepageData) return null;
  const menuBar = homepageData.menu_bar;
  const prefix  = config.PREFIX;
  const data    = smallAutomationsData;

  // 'page.items' = same items as the homepage boxes, but with the page-only fields
  // (body, examples, image_size) and the yaml 'format_page' overlay applied.
  const page  = (data && data.page) || {};
  const items = page.items || {};

  return (
    <div className="w4u-page">
      <Navigation menuBar={menuBar} prefix={prefix} />
      <main className="w4u-main">
        {!data && <div className="w4u-loading">Loading…</div>}
        {data && (
          <>
            <SaHeader header={page.page_header} />
            <section className="w4u-sa__list">
              {Object.entries(items).map(([tag, item]) => (
                <SaItemCard key={tag} tag={tag} item={item} examplesLabel={page.examples_label} />
              ))}
            </section>
            <SaCta cta={page.cta} />
          </>
        )}
      </main>
      <Footer menuBar={menuBar} config={config} contactData={contactData} footer={homepageData.footer} />
    </div>
  );
}

// ----- Page header -----

const SaHeader = ({ header }) => {
  if (!header) return null;
  return (
    <section className="w4u-sa__header" style={getBottomMargin(header.bottom_margin)}>
      <h1 className="w4u-sa__title" style={getStyle(header.title)}>{RT(header.title)}</h1>
      {header.subtitle && (
        <p className="w4u-sa__subtitle" style={getStyle(header.subtitle)}>{RT(header.subtitle)}</p>
      )}
      {header.subtitle_2 && (
        <p className="w4u-sa__subtitle2" style={getStyle(header.subtitle_2)}>{RT(header.subtitle_2)}</p>
      )}
    </section>
  );
};

// ----- The examples block (label + pills) -----
// Rendered twice per card: variant 'desktop' lives inside the text column (next to the icon),
// variant 'mobile' is a sibling under the whole icon/title/header group. CSS shows one at a time.

const SaExamples = ({ examples, examplesLabel, variant }) => {
  if (examples.length === 0) return null;
  return (
    <div className={`w4u-sa__examples-block w4u-sa__examples-block--${variant}`}>
      {examplesLabel && (
        <p className="w4u-sa__examples-label" style={getStyle(examplesLabel)}>{RT(examplesLabel)}</p>
      )}
      <ul className="w4u-sa__examples">
        {examples.map((ex, i) => (
          <li key={i} className="w4u-sa__example-pill" style={getStyle(ex)}>{RT(ex)}</li>
        ))}
      </ul>
    </div>
  );
};

// ----- One automation card -----

const SaItemCard = ({ tag, item, examplesLabel }) => {
  // icon size: yaml-driven (square default, per-item override); mobile via CSS vars
  const size = item.image_size || {};
  const iconStyle = {};
  if (size.width)         iconStyle.width  = size.width;
  if (size.height)        iconStyle.height = size.height;
  if (size.width_mobile)  iconStyle['--icon-w-mobile'] = size.width_mobile;
  if (size.height_mobile) iconStyle['--icon-h-mobile'] = size.height_mobile;

  const examples = Array.isArray(item.examples) ? item.examples : [];

  return (
    <article id={tag} className="w4u-sa__card" style={getBottomMargin(item.bottom_margin)}>
      <div className="w4u-sa__card-top">
        {item.image_path && (
          <div className="w4u-sa__icon-box">
            <img src={item.image_path} alt="" className="w4u-sa__icon" style={iconStyle} />
          </div>
        )}
        <div className="w4u-sa__card-main">
          <h2 className="w4u-sa__card-title" style={getStyle(item.title)}>{RT(item.title)}</h2>
          {item.header && (
            <p className="w4u-sa__card-header" style={getStyle(item.header)}>{RT(item.header)}</p>
          )}
          <SaExamples examples={examples} examplesLabel={examplesLabel} variant="desktop" />
        </div>
      </div>
      <SaExamples examples={examples} examplesLabel={examplesLabel} variant="mobile" />
      {item.body && (
        <p className="w4u-sa__card-body" style={getStyle(item.body)}>{RT(item.body)}</p>
      )}
    </article>
  );
};

// ----- CTA -----

const SaCta = ({ cta }) => {
  if (!cta) return null;
  return (
    <section className="w4u-sa__cta" style={getBottomMargin(cta.bottom_margin)}>
      {cta.line_1 && <p className="w4u-sa__cta-line" style={getStyle(cta.line_1)}>{RT(cta.line_1)}</p>}
      {cta.punch_line && <p className="w4u-sa__cta-punch" style={getStyle(cta.punch_line)}>{RT(cta.punch_line)}</p>}
      <OrangeButton to="/contact" variant="hero" style={getStyle(cta.button_text)}>
        {getText(cta.button_text) ? RT(cta.button_text) : 'Contact us'}
      </OrangeButton>
    </section>
  );
};

export default BackendPageForSA;
